import { createEnum } from "@redotech/zod/zod-enum";
import * as z from "zod/v4";
import { WebhookEventType } from "../integrations/returns/blueyonder/blueyonder";
import {
  BLUE_YONDER_PROVIDER,
  type DropoffProviderType,
  RETURN_BEAR_PROVIDER,
  VIRTUAL_PROVIDER,
} from "./dropoff-provider";

// =============================================================================
// Address
// =============================================================================

const DropoffLocationAddressSchema_ = z.object({
  street1: z.string(),
  street2: z.string().optional(),
  city: z.string(),
  state: z.string(),
  zip: z.string(),
  country: z.string(),
});
export const DropoffLocationAddressSchema: z.ZodType<DropoffLocationAddress> =
  DropoffLocationAddressSchema_;
export interface DropoffLocationAddress
  extends z.infer<typeof DropoffLocationAddressSchema_> {}

// =============================================================================
// Dropoff Location Result (per-provider discriminated union)
// =============================================================================

const HoursEntrySchema_ = z.object({ open: z.string(), close: z.string() });

const VirtualLocationMetadataSchema_ = z.object({
  phoneNumber: z.string().optional(),
  hours: z
    .object({
      sun: z.array(HoursEntrySchema_).optional(),
      mon: z.array(HoursEntrySchema_).optional(),
      tue: z.array(HoursEntrySchema_).optional(),
      wed: z.array(HoursEntrySchema_).optional(),
      thu: z.array(HoursEntrySchema_).optional(),
      fri: z.array(HoursEntrySchema_).optional(),
      sat: z.array(HoursEntrySchema_).optional(),
    })
    .optional(),
});
export const VirtualLocationMetadataSchema: z.ZodType<VirtualLocationMetadata> =
  VirtualLocationMetadataSchema_;
export interface VirtualLocationMetadata
  extends z.infer<typeof VirtualLocationMetadataSchema_> {}

const dropoffLocationResultBase = {
  id: z.string(),
  name: z.string(),
  address: DropoffLocationAddressSchema,
  distance: z.string().describe("e.g., '1.2 miles', MUST BE IN MILES"),
  geo: z.object({ latitude: z.number(), longitude: z.number() }),
};

const BlueYonderHoursEntrySchema_ = z.object({
  isOpen: z.boolean(),
  hours: z.array(z.tuple([z.string(), z.string()])).nullish(),
});

const BlueYonderOpeningHoursSchema_ = z.object({
  monday: BlueYonderHoursEntrySchema_,
  tuesday: BlueYonderHoursEntrySchema_,
  wednesday: BlueYonderHoursEntrySchema_,
  thursday: BlueYonderHoursEntrySchema_,
  friday: BlueYonderHoursEntrySchema_,
  saturday: BlueYonderHoursEntrySchema_,
  sunday: BlueYonderHoursEntrySchema_,
});

const BlueYonderLocationMetadataSchema_ = z.object({
  // The merchant's configured BlueYonder return destination location ID.
  // Sourced from the merchant's address (blueYonderLocationId) and passed to
  // the BlueYonder createReturn API as destinationLocation.locationId.
  blueYonderLocationId: z.string().optional(),
  locationType: z.string().optional(),
  phoneNumber: z.string().optional(),
  openingHours: BlueYonderOpeningHoursSchema_.optional(),
});
export const BlueYonderLocationMetadataSchema: z.ZodType<BlueYonderLocationMetadata> =
  BlueYonderLocationMetadataSchema_;
export interface BlueYonderLocationMetadata
  extends z.infer<typeof BlueYonderLocationMetadataSchema_> {}

const DropoffLocationResultSchema_ = z.discriminatedUnion("provider", [
  z.object({
    ...dropoffLocationResultBase,
    provider: z.literal(VIRTUAL_PROVIDER),
    providerMetadata: VirtualLocationMetadataSchema.optional(),
  }),
  z.object({
    ...dropoffLocationResultBase,
    provider: z.literal(BLUE_YONDER_PROVIDER),
    providerMetadata: BlueYonderLocationMetadataSchema.optional(),
  }),
  z.object({
    ...dropoffLocationResultBase,
    provider: z.literal(RETURN_BEAR_PROVIDER),
    providerMetadata: z.object({}).optional(),
  }),
]);
export const DropoffLocationResultSchema: z.ZodType<DropoffLocationResult> =
  DropoffLocationResultSchema_;
export type DropoffLocationResult = {} & z.infer<
  typeof DropoffLocationResultSchema_
>;

export const DROPOFF_PROVIDER_LOCATION_METADATA_SCHEMAS = {
  VIRTUAL: VirtualLocationMetadataSchema,
  RETURN_BEAR: z.object({}),
  BLUE_YONDER: BlueYonderLocationMetadataSchema,
} satisfies Record<DropoffProviderType, z.ZodType>;

// =============================================================================
// Get Dropoff Locations Result
// =============================================================================

const GetDropoffLocationsResultSchema_ = z.object({
  locations: z.array(DropoffLocationResultSchema),
});
export const GetDropoffLocationsResultSchema: z.ZodType<GetDropoffLocationsResult> =
  GetDropoffLocationsResultSchema_;
export interface GetDropoffLocationsResult
  extends z.infer<typeof GetDropoffLocationsResultSchema_> {}

// =============================================================================
// Dropoff QR Code Result (per-provider discriminated union)
// =============================================================================

const VirtualQrCodeMetadataSchema_ = z.object({
  dashCode: z.string().optional(),
  expiresAt: z.number().optional(),
  returnVerification: z.literal(true).optional(),
  uniUniOrderNumber: z.string().optional(),
  uniUniTrackingId: z.string().optional(),
  uniUniLabelUrl: z.string().optional(),
});
export const VirtualQrCodeMetadataSchema: z.ZodType<VirtualQrCodeMetadata> =
  VirtualQrCodeMetadataSchema_;
export interface VirtualQrCodeMetadata
  extends z.infer<typeof VirtualQrCodeMetadataSchema_> {}

const dropoffQrCodeResultBase = {
  shipmentGroupId: z.string(),
  externalShipmentId: z.string().optional(),
  qrCode: z.string(),
  qrCodeUrl: z.string(),
  trackingNumber: z.string().nullish(),
  dropoffFlatRateFee: z.number().nullish(),
};

const BlueYonderWebhookEventSchema = z.object({
  eventType: createEnum(WebhookEventType),
  status: z.string(),
  eventDateTime: z.coerce.date(),
  eventId: z.string(),
});

export type BlueYonderWebhookEvent = z.infer<
  typeof BlueYonderWebhookEventSchema
>;

const BlueYonderQrCodeMetadataSchema_ = z.object({
  blueYonderLocationId: z.string().nullish(),
  returnId: z.string().nullish(),
  labelValue: z.string().nullish(),
  caseId: z.string().nullish(),
  returnAuthorizationNumber: z.string().nullish(),
  webhookEvents: z.array(BlueYonderWebhookEventSchema).optional(),
});
export const BlueYonderQrCodeMetadataSchema: z.ZodType<BlueYonderQrCodeMetadata> =
  BlueYonderQrCodeMetadataSchema_;
export interface BlueYonderQrCodeMetadata
  extends z.infer<typeof BlueYonderQrCodeMetadataSchema_> {}

const DropoffQrCodeResultSchema_ = z.discriminatedUnion("provider", [
  z.object({
    ...dropoffQrCodeResultBase,
    provider: z.literal(VIRTUAL_PROVIDER),
    providerMetadata: VirtualQrCodeMetadataSchema,
  }),
  z.object({
    ...dropoffQrCodeResultBase,
    provider: z.literal(BLUE_YONDER_PROVIDER),
    providerMetadata: BlueYonderQrCodeMetadataSchema,
  }),
  z.object({
    ...dropoffQrCodeResultBase,
    provider: z.literal(RETURN_BEAR_PROVIDER),
    providerMetadata: z.object({}),
  }),
]);
export const DropoffQrCodeResultSchema: z.ZodType<DropoffQrCodeResult> =
  DropoffQrCodeResultSchema_;
export type DropoffQrCodeResult = {} & z.infer<
  typeof DropoffQrCodeResultSchema_
>;

export const DROPOFF_PROVIDER_METADATA_SCHEMAS = {
  VIRTUAL: VirtualQrCodeMetadataSchema,
  RETURN_BEAR: z.object({}),
  BLUE_YONDER: BlueYonderQrCodeMetadataSchema,
} satisfies Record<DropoffProviderType, z.ZodType>;
